import { useCallback, useRef, type ReactNode } from 'react';
import { Button, I, type ButtonSize, type ButtonVariant } from '@leanin/ui';
import { HIDDEN_FILE_INPUT_STYLE } from '@leanin/ui/client';

const ACCEPT =
  'image/png,image/jpeg,image/webp,image/gif,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type Props = {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  /** Render as an icon-only `IconButton` (default). Set to `inline` to show a
   *  text button alongside the icon. */
  variant?: 'icon' | 'inline';
  label?: string;
  /** Override the `accept` attribute when a caller needs a wider/narrower
   *  file-type allowlist than the default post-media set. Empty / undefined
   *  falls back to the default. */
  accept?: string;
  /** Allow picking only one file at a time when set to false. Defaults to
   *  true (multi-pick) to match the post composer's flow. */
  multiple?: boolean;
  /** Override the `inline` variant's Button visual treatment. Defaults to
   *  `secondary` (the post composer's affordance). Set to `primary` when the
   *  CTA wants the brand-burgundy fill — e.g. the network Resources page,
   *  where leaders need a clearer call-to-action than the composer's icon. */
  buttonVariant?: ButtonVariant;
  /** Override the trigger Button's size (both variants). Defaults to `sm`
   *  so composer toolbars stay compact; pass `md` when the trigger sits
   *  next to an md-tier control (a header CTA, or the DM composer's send
   *  button) and must share its vertical center. */
  buttonSize?: ButtonSize;
  /** Trigger glyph. Defaults to the image glyph; pass a document glyph when
   *  the button's `accept` list is document types. */
  icon?: ReactNode;
};

/**
 * Visible trigger + visually-hidden file input. The input remains keyboard-
 * focusable via `clip-path: inset(50%)` (never `display: none`) so screen-
 * reader users can pick files without the click handler shim.
 */
export function PostMediaUploadButton({
  onFiles,
  disabled = false,
  variant = 'icon',
  label = 'Upload media',
  accept,
  multiple = true,
  buttonVariant = 'secondary',
  buttonSize = 'sm',
  icon = I.image,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onPick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      // Reset the value so picking the same file twice still fires.
      event.target.value = '';
      if (files.length > 0) onFiles(files);
    },
    [onFiles],
  );

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept ?? ACCEPT}
        onChange={onChange}
        disabled={disabled}
        style={HIDDEN_FILE_INPUT_STYLE}
      />
      {variant === 'icon' ? (
        <Button
          variant="ghost"
          size={buttonSize}
          icon={icon}
          aria-label={label}
          onClick={onPick}
          disabled={disabled}
        />
      ) : (
        <Button
          variant={buttonVariant}
          size={buttonSize}
          icon={icon}
          onClick={onPick}
          disabled={disabled}
        >
          {label}
        </Button>
      )}
    </>
  );
}
